import { RouteObject } from 'react-router';

import { ROUTES } from '~shared/routes';

import { AuthenticatedRoute } from '../../components/AuthenticatedRoute';
import { RoleGuard } from '../../components/guards/RoleGuard';
import { RoleBasedIndexRedirect } from '../../components/redirect/RoleBasedIndexRedirect';
import { notFoundRoute } from '../not-found';
import { catalogRoutes } from './catalog';
import { contentRoutes } from './content';
import { deliveryRoutes } from './delivery';
import { formsRoutes } from './forms';
import { jobsRoutes } from './jobs';
import { meetingsRoutes } from './meetings';
import { ordersRoutes } from './orders';
import { patientRoutes } from './patient';
import { promotionsRoutes } from './promotions';
import { waitingListRoutes } from './waitingList';

export const authenticatedRoutes: RouteObject = {
  element: <AuthenticatedRoute />,
  children: [
    // --------------------------------------------------------
    // REDIRECT FOR INDEX
    // --------------------------------------------------------
    {
      index: true,
      element: (
        <RoleBasedIndexRedirect
          roleRedirects={{
            ANIMATOR: ROUTES.meetings.root,
          }}
          defaultRedirect={ROUTES.dashboard}
        />
      ),
    },

    // --------------------------------------------------------
    // ROUTES AVAILABLE TO ALL (EVEN THE ANIMATOR)
    // --------------------------------------------------------
    meetingsRoutes,

    // --------------------------------------------------------
    // PROTECTED ROUTES (FOR ALL EXCEPT THE ANIMATOR)
    // --------------------------------------------------------
    {
      element: (
        <RoleGuard
          allowedRoles={[
            'ADMINISTRATOR',
            'PHARMACIST',
            'ASSISTANT',
            'PHARMACIST_SUPPORT',
            'TECHNICAL_SUPPORT',
            'GROUP',
          ]}
          redirectTo={ROUTES.meetings.root}
        />
      ),
      children: [
        {
          path: ROUTES.dashboard,
          lazy: async () => {
            const { PerformancesPage } =
              await import('../../../pages/performances/Performances.page');

            return { Component: PerformancesPage };
          },
        },
        {
          path: ROUTES.profile,
          lazy: async () => {
            const { ProfilPage } =
              await import('../../../pages/profil/Profil.page');

            return { Component: ProfilPage };
          },
        },
        {
          path: ROUTES.cncConfiguration,
          lazy: async () => {
            const { CncConfigPage } =
              await import('../../../pages/cnc-config/CncConfig.page');

            return { Component: CncConfigPage };
          },
        },
        {
          path: ROUTES.synchronization,
          lazy: async () => {
            const { SynchronizationPage } =
              await import('../../../pages/synchronization/Synchronization.page');

            return { Component: SynchronizationPage };
          },
        },
        {
          path: ROUTES.communication,
          lazy: async () => {
            const { CommunicationPage } =
              await import('../../../pages/communication/Communication.page');

            return { Component: CommunicationPage };
          },
        },
        {
          path: ROUTES.messaging,
          lazy: async () => {
            const { ThreadPage } =
              await import('../../../pages/thread/Thread.page');

            return { Component: ThreadPage };
          },
        },
        {
          path: ROUTES.onDuty,
          lazy: async () => {
            const { OnDutyPage } =
              await import('../../../pages/on-duty/OnDuty.page');

            return { Component: OnDutyPage };
          },
        },
        {
          path: ROUTES.forum,
          lazy: async () => {
            const { IntranetPage } =
              await import('../../../pages/intranet/Intranet.page');

            return { Component: IntranetPage };
          },
        },
        {
          path: ROUTES.campaigns,
          lazy: async () => {
            const { CampaignPage } =
              await import('../../../pages/campaign/Campaign.page');

            return { Component: CampaignPage };
          },
        },
        {
          path: ROUTES.accessConfiguration,
          lazy: async () => {
            const { AccessUserPage } =
              await import('../../../pages/access/AccessUser.page');

            return { Component: AccessUserPage };
          },
        },
        {
          path: ROUTES.myPharmacy,
          lazy: async () => {
            const { PharmacyConfigurationPage } =
              await import('../../../pages/pharmacy-configuration');

            return { Component: PharmacyConfigurationPage };
          },
        },
        {
          path: ROUTES.team,
          lazy: async () => {
            const { MembersPage } =
              await import('../../../pages/members/Members.page');

            return { Component: MembersPage };
          },
        },
        {
          path: ROUTES.linings,
          lazy: async () => {
            const { LiningsPage } =
              await import('../../../pages/linings/Linings.page');

            return { Component: LiningsPage };
          },
        },
        {
          path: ROUTES.services,
          lazy: async () => {
            const { ServicesPage } =
              await import('../../../pages/services/Services.page');

            return { Component: ServicesPage };
          },
        },
        {
          path: ROUTES.specialities,
          lazy: async () => {
            const { SpecialitiesPage } =
              await import('../../../pages/specialities/Specialities.page');

            return { Component: SpecialitiesPage };
          },
        },
        {
          path: ROUTES.images,
          lazy: async () => {
            const { ImagesPage } =
              await import('../../../pages/images/Images.page');

            return { Component: ImagesPage };
          },
        },
        {
          path: ROUTES.videos,
          lazy: async () => {
            const { VideosPage } =
              await import('../../../pages/videos/Videos.page');

            return { Component: VideosPage };
          },
        },
        {
          path: ROUTES.tips,
          lazy: async () => {
            const { TipsPage } = await import('../../../pages/tips/Tips.page');

            return { Component: TipsPage };
          },
        },
        {
          path: ROUTES.sections,
          children: [
            {
              index: true,
              lazy: async () => {
                const { SectionsPage } =
                  await import('../../../pages/sections/Sections.page');

                return { Component: SectionsPage };
              },
            },
            {
              path: ROUTES.sectionCreate,
              lazy: async () => {
                const { SectionFormPage } =
                  await import('../../../pages/sections/SectionForm.page');

                return { Component: SectionFormPage };
              },
            },
            {
              path: ROUTES.sectionEdit,
              lazy: async () => {
                const { SectionFormPage } =
                  await import('../../../pages/sections/SectionForm.page');

                return { Component: SectionFormPage };
              },
            },
          ],
        },
        {
          path: ROUTES.notices,
          lazy: async () => {
            const { NoticesPage } =
              await import('../../../pages/notices/Notices.page');

            return { Component: NoticesPage };
          },
        },
        {
          path: ROUTES.configure,
          lazy: async () => {
            const { PharmacyConfigPage } =
              await import('../../../pages/pharmacy-config/PharmacyConfig.page');

            return { Component: PharmacyConfigPage };
          },
        },
        {
          path: ROUTES.diag,
          lazy: async () => {
            const { CncConfigDiagnosticPage } =
              await import('../../../pages/cnc-config/CncConfigDiagnostic.page');

            return { Component: CncConfigDiagnosticPage };
          },
        },

        // ORDERS
        ordersRoutes,

        // CATALOG
        catalogRoutes,

        // PROMOTIONS
        promotionsRoutes,

        // WAITING LIST
        waitingListRoutes,

        // FORMS
        formsRoutes,

        // CONTENT
        contentRoutes,

        // PATIENT
        patientRoutes,

        // DELIVERY
        deliveryRoutes,

        // JOBS
        jobsRoutes,

        notFoundRoute,
      ],
    },
  ],
};
